/**
 * The game (spec 08): the view, the session, the menus, the controls and the soundtrack, run on a
 * fixed 50 Hz simulation clock (slower in practice speed) with the picture drawn at the display's own
 * rate, between sim frames. The player's options come from `SettingsStore` and apply live.
 *
 *   /                      the title
 *   /?level=5&players=2    start at once on level 5 with two players
 *   /?ui=options           open a menu screen (title, options, controls, credits, changes)
 *   &look=1987&crt=1&lock=0.6&px=2&aid=1&noflash=1   display options for this visit only (not saved)
 *   F (or ?fps=1)          the frame rate and draw time, bottom left
 */

import { Keyboard, readCommands } from '../../input';
import { level } from '../../levels';
import { GameView } from '../../render/game/GameView';
import { levelLook } from '../../render/levelLook';
import { Palette } from '../../render/palette';
import { renderSize } from '../../render/size';
import { type DisplaySettings, type Settings, SettingsStore } from '../../settings';
import type { MenuScreen } from '../../ui/menu';
import { chooseSize, createRenderer, num } from '../devKit';
import { exposeGame } from './debugHooks';
import { FrameMeter } from './frameMeter';
import { FrontEnd } from './front';
import { hudState, sessionBanners } from './hudState';
import { MusicDirector } from './music';
import { Session } from './session';

const FRAME_MS = 1000 / 50;
/** Never run more than this many sim frames to catch up (a background tab, a debugger pause). */
const MAX_CATCH_UP = 5;
const SCREENS: readonly MenuScreen[] = ['title', 'options', 'controls', 'credits', 'changes'];

/** Display options from the URL, for this visit only (captures and checks). */
function displayFromUrl(params: URLSearchParams): Partial<DisplaySettings> {
  const d: Partial<DisplaySettings> = {};
  if (params.get('look') === '1987' || params.get('look') === 'modern') d.look = params.get('look') as DisplaySettings['look'];
  if (params.has('crt')) d.crt = params.get('crt') === '1';
  if (params.has('lock')) d.paletteLock = Math.min(1, Math.max(0, num(params, 'lock', 0.25)));
  const px = num(params, 'px', 0);
  if (px === 1 || px === 2 || px === 3) d.pixelSize = px;
  if (params.has('aid')) d.colourAid = params.get('aid') === '1';
  if (params.has('noflash')) d.reduceFlashing = params.get('noflash') === '1';
  return d;
}

export function startGame(canvas: HTMLCanvasElement, params: URLSearchParams): void {
  const settings = new SettingsStore();
  const urlDisplay = displayFromUrl(params);
  const display = (s: Settings): DisplaySettings => ({ ...s.display, ...urlDisplay });
  const sizeParams = new URLSearchParams(params);
  if (!sizeParams.has('px')) sizeParams.set('px', String(display(settings.get()).pixelSize));

  const renderer = createRenderer(canvas);
  const startLevel = Math.max(1, Math.min(100, Math.round(num(params, 'level', 1))));
  const view = new GameView(renderer, chooseSize(sizeParams), level(startLevel));
  const music = new MusicDirector();
  const session = new Session(view, music, startLevel);
  const keys = new Keyboard();
  const meter = new FrameMeter(params.get('fps') === '1');
  // A forced internal size (captures) stays forced when the pixel size changes.
  const res = num(params, 'res', 0);
  if (res > 0) view.sizeFor = () => renderSize(res / 640);
  const palette = new Palette(levelLook(level(1)));
  const ink = palette.hex('ink.0');
  const warn = palette.hex('angry.2');
  const front = new FrontEnd(session, settings, keys, view, () => ({ ...view.panelColours, panel: ink, shadow: ink, warn }));
  const apply = (s: Settings): void => {
    view.setDisplay(display(s));
    keys.setBindings(s.keys);
    music.setAudio(s.audio);
  };
  apply(settings.get());
  settings.subscribe(apply);

  // Audio may only start after the player touches something.
  const wake = (): void => music.wake();
  window.addEventListener('keydown', wake);
  window.addEventListener('pointerdown', wake);
  if (params.has('players')) {
    front.menus.close();
    session.start([true, num(params, 'players', 1) >= 2]);
  } else {
    music.title();
    const ui = SCREENS.find((s) => s === params.get('ui'));
    if (ui) front.menus.open(ui);
  }

  let last = performance.now();
  let acc = 0;
  const frame = (now: number): void => {
    acc += now - last;
    last = now;
    let steps = 0;
    const s = settings.get();
    const frameMs = FRAME_MS / s.play.gameSpeed;
    if (!front.menus.capturing && keys.take('KeyF')) meter.shown = !meter.shown;
    // The C64's SUPORR chord on the title (02 §9.5; C= has no PC key, so the five letters do).
    if (session.mode === 'title' && keys.isDown(['KeyS']) && keys.isDown(['KeyU']) && keys.isDown(['KeyP'])
      && keys.isDown(['KeyO']) && keys.isDown(['KeyR'])) session.suporr = true;
    while (acc >= frameMs && steps < MAX_CATCH_UP) {
      front.frame();
      if (s.play.gameSpeed < 1 && session.mode === 'play') session.practice = true;
      session.tick(readCommands(keys, s.keys));
      // The players read the controls on I-ticks only, every other frame: a tap is kept until one
      // has seen it, or a quick press landing on the frame between would be lost.
      if (session.mode !== 'play' || session.paused || session.world?.clock.iTick !== false) keys.endFrame();
      acc -= frameMs;
      steps++;
    }
    if (steps === MAX_CATCH_UP) acc = 0;
    const drawStart = performance.now();
    view.setTransition(session.transition.cover(acc / frameMs));
    view.render(session.world, session.prev, acc / frameMs, now / 1000, hudState(session),
      sessionBanners(session, display(s).reduceFlashing, front.menus.covering), (ctx) => front.draw(ctx));
    meter.frame(now, performance.now() - drawStart);
    meter.paint(view.hud.canvas.getContext('2d'));
    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);
  exposeGame(session, view, music, front, settings);
}
